/**
 * StepEntryPoint.jsx
 *
 * Tool Finder hub — first wizard step. Mirrors the layout of the production
 * site at https://rocktools.my.sandvik/s/toolfinder?language=en_US:
 *
 *   1) Page header — large "Tool Finder" title (left) + descriptive
 *      paragraph (right). Two-column on desktop, stacked on mobile.
 *   2) Two hero sections side-by-side, each containing:
 *       - A hero image placeholder (16:10 gray block — real marcom imagery
 *         drops in later; the slot is intentionally explicit so designers
 *         can find it).
 *       - A hero title.
 *       - A hero description.
 *       - 2 path tiles below the hero.
 *   3) Four path tiles total, all wired from this single page:
 *       - "I have a Sandvik system"  → wizard EXPLORE path
 *       - "I have a drill rig"       → wizard REPLACE path
 *       - "Top Hammer"               → /drill-string-configurator?type=top-hammer
 *       - "Down-the-Hole"            → /drill-string-configurator?type=dth
 *
 * There is intentionally NO intermediate TH/DTH selection page between the
 * hub and the DSC layouts. Both DSC paths route directly with the type query
 * already set (rule captured in design-rules-from-walkthrough.md).
 *
 * No font-bold anywhere — Sandvik Sans regular weight already carries the
 * bold feel (Marcom rule, 20 May 2026 walkthrough).
 *
 * @module components/wizard/StepEntryPoint
 * @since 2026-05-15 (Group D Sub-Plan 1, Task D1)
 *        Hub-layout rewrite: 2026-05-20 (post-walkthrough UX batch).
 */

import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useWizard } from '@contexts/WizardContext';
import { ENTRY_PATHS } from '@components/wizard/wizardConstants';

// ---------------------------------------------------------------------------
// Path tile — eyebrow label + underlined action link + chevron.
// Used inside each hero section.
// ---------------------------------------------------------------------------

function PathTile({ eyebrow, action, onClick, to, testid }) {
  const inner = (
    <>
      <div className="flex flex-col">
        <span className="text-xs font-sandvik-text text-n6 tracking-wide">
          {eyebrow}
        </span>
        <span className="mt-1 text-base font-sandvik-text text-carbon-black underline underline-offset-4">
          {action}
        </span>
      </div>
      <ChevronRight
        size={20}
        className="text-vibrant-orange shrink-0 ml-3 transition-transform group-hover:translate-x-1"
        aria-hidden="true"
      />
    </>
  );

  const classes =
    'group flex items-center justify-between gap-3 px-4 py-3 border border-n3 bg-n1 hover:border-vibrant-orange hover:bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-vibrant-orange';

  if (to) {
    return (
      <Link to={to} className={classes} data-testid={testid} style={{ borderRadius: 0 }}>
        {inner}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={classes}
      data-testid={testid}
      style={{ borderRadius: 0 }}
    >
      {inner}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Hero section — image placeholder + title + description + 2 path tiles.
// ---------------------------------------------------------------------------

function HeroSection({ imageLabel, title, description, tiles, testid }) {
  return (
    <section className="flex flex-col gap-4" data-testid={testid}>
      {/* Hero image slot — gray placeholder with a label indicating the slot.
          Real marcom imagery drops in here later. Aspect ~16:10 to match
          the production site. */}
      <div
        className="relative w-full bg-gradient-to-br from-n3 to-n4 flex items-center justify-center"
        style={{ aspectRatio: '16 / 10', borderRadius: 0 }}
        aria-hidden="true"
        data-testid={`${testid}-image-placeholder`}
      >
        <span className="text-white text-sm font-sandvik-text tracking-wider px-3 py-1 bg-carbon-black/40">
          {imageLabel}
        </span>
      </div>
      <h2 className="font-sandvik-headline text-2xl text-carbon-black">
        {title}
      </h2>
      <p className="font-sandvik-text text-sm text-n6 leading-relaxed">
        {description}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
        {tiles}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function StepEntryPoint() {
  const { handleEntryPathSelect } = useWizard();

  return (
    <div data-testid="tool-finder-hub" className="-mx-4">
      {/* Section 1 — Page header on white background. */}
      <div className="bg-white">
        <div className="sandvik-container pt-10 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-start">
            <h1 className="font-sandvik-headline text-5xl lg:text-6xl text-carbon-black leading-none">
              Tool Finder
            </h1>
            <p className="font-sandvik-text text-base text-n7 leading-relaxed lg:pt-3">
              Need help finding the right tool or are you unsure of which Rock
              Tools can be configured together? If that is the case, then you
              have come to the right page. Select the scenario you need help
              with, and we will provide you with a set of guided questions to
              help you choose the right Rock Tools for your requirements.
            </p>
          </div>
        </div>
      </div>

      {/* Section 2 — Heroes + path tiles, on subtly tinted background to
          differentiate from the header section. Matches the production
          site's two-tone treatment. */}
      <div className="bg-n1 border-t border-n2">
        <div className="sandvik-container py-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
            <HeroSection
              testid="hero-find-tool"
              imageLabel="Hero image — workers with tablet"
              title="Help me find the right tool"
              description="If you know what kind of drilling application, rock conditions, hole size you wish to drill, then choose the first option."
              tiles={
                <>
                  <PathTile
                    testid="path-explore"
                    eyebrow="I have a Sandvik system"
                    action="I need to find a rock tool"
                    onClick={() => handleEntryPathSelect(ENTRY_PATHS.EXPLORE)}
                  />
                  <PathTile
                    testid="path-replace"
                    eyebrow="I have a drill rig"
                    action="I need to find a replacement"
                    onClick={() => handleEntryPathSelect(ENTRY_PATHS.REPLACE)}
                  />
                </>
              }
            />
            <HeroSection
              testid="hero-configure-string"
              imageLabel="Hero image — Sandvik rig on site"
              title="I want to configure a drill string"
              description="Choose this option when you need help knowing which Rock Tools can be configured together. First, select the drilling type."
              tiles={
                <>
                  <PathTile
                    testid="path-dsc-th"
                    eyebrow="Top Hammer"
                    action="Go to TH configurator"
                    to="/drill-string-configurator?type=top-hammer"
                  />
                  <PathTile
                    testid="path-dsc-dth"
                    eyebrow="Down-the-Hole"
                    action="Go to DTH configurator"
                    to="/drill-string-configurator?type=dth"
                  />
                </>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
